import React, { useEffect, useState } from "react";
import { FiX, FiMail } from "react-icons/fi";
import style from "./SendEmailModal.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  userAnnouncementPreview,
  userAnnouncementSend,
  userAnnouncementTest,
} from "../../api/HttpApis";

/**
 * Compose an announcement email to the users picked on the Users list.
 *
 * On open it asks the backend who will actually receive it: only users with a
 * verified email who haven't unsubscribed get mail (same rule as the
 * new-content blast), so the admin sees how many are skipped before sending.
 * It also shows whether the email queue worker is running, since queued mail
 * only goes out while it is, and offers a test send to the admin's own inbox.
 *
 * props:
 *   open, audience ({ user_ids } | { all_matching, filters }), selectedLabel,
 *   onClose, onSent
 */
const SendEmailModal = ({ open, audience, selectedLabel, onClose, onSent }) => {
  const [subject, setSubject] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [counts, setCounts] = useState(null);
  const [countError, setCountError] = useState("");
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  // Where "Send test" goes; blank = the backend default (Subscribers inbox from
  // Settings → Email routing, else SMTP From, else your account email).
  const [testEmail, setTestEmail] = useState("");

  // Escape closes, and the page behind must not scroll while this is up.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !sending) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, sending]);

  useEffect(() => {
    if (!open || !audience) return;
    let cancelled = false;
    setCounts(null);
    setCountError("");
    userAnnouncementPreview(audience)
      .then((res) => {
        if (!cancelled) setCounts(res?.data?.data || null);
      })
      .catch((error) => {
        if (!cancelled) {
          setCountError(
            error?.response?.data?.message || "Couldn't count recipients."
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, audience]);

  if (!open) return null;

  const recipients = counts?.recipients ?? 0;
  const queue = counts?.queue;
  const queueDown = queue && !queue.redis_ok;
  const hasDraft = Boolean(subject.trim() && message.trim());
  const canSend =
    !sending && !testing && counts && recipients > 0 && !queueDown && hasDraft;
  const canTest = !sending && !testing && hasDraft;

  const showError = (error, fallback) => {
    const response = error?.response?.data;
    const fieldErrors = response?.errors
      ? Object.values(response.errors).flat()
      : [];
    showToast(fieldErrors[0] || response?.message || fallback, "error");
  };

  const handleTest = async () => {
    if (!canTest) return;
    setTesting(true);
    try {
      const res = await userAnnouncementTest({
        subject: subject.trim(),
        title: title.trim(),
        message: message.trim(),
        ...(testEmail.trim() && { test_email: testEmail.trim() }),
      });
      showToast(res?.data?.message || "Test email sent.", "success");
    } catch (error) {
      showError(error, "Failed to send test email.");
    } finally {
      setTesting(false);
    }
  };

  const handleSend = async () => {
    if (!canSend) return;
    const confirmed = window.confirm(
      `Send "${subject.trim()}" to ${recipients.toLocaleString()} user${
        recipients === 1 ? "" : "s"
      }? This can't be undone.`
    );
    if (!confirmed) return;

    setSending(true);
    try {
      const res = await userAnnouncementSend({
        ...audience,
        subject: subject.trim(),
        title: title.trim(),
        message: message.trim(),
      });
      showToast(res?.data?.message || "Email queued.", "success");
      setSubject("");
      setTitle("");
      setMessage("");
      onSent?.();
    } catch (error) {
      showError(error, "Failed to send email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={style.overlay}
      onClick={sending ? undefined : onClose}
      role="presentation"
    >
      <div
        className={style.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Send email"
      >
        <div className={style.header}>
          <div className={style.headTitle}>
            <span className={style.icon}>
              <FiMail size={17} />
            </span>
            <div>
              <h3 className={style.title}>Send email</h3>
              {selectedLabel && <p className={style.sub}>{selectedLabel}</p>}
            </div>
          </div>
          <button
            type="button"
            className={style.closeBtn}
            onClick={onClose}
            disabled={sending}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className={style.body}>
          <div className={style.recipients}>
            {countError ? (
              <span className={style.error}>{countError}</span>
            ) : !counts ? (
              <span className={style.muted}>Counting recipients…</span>
            ) : (
              <>
                <strong>
                  {recipients.toLocaleString()} will receive this email
                </strong>
                {counts.included_unverified > 0 && (
                  <span className={style.muted}>
                    Includes{" "}
                    {counts.included_unverified.toLocaleString()} with email
                    not verified
                  </span>
                )}
                {(counts.excluded_unverified > 0 ||
                  counts.excluded_unsubscribed > 0) && (
                  <span className={style.muted}>
                    Skipped:{" "}
                    {[
                      counts.excluded_unverified > 0 &&
                        `${counts.excluded_unverified.toLocaleString()} email not verified`,
                      counts.excluded_unsubscribed > 0 &&
                        `${counts.excluded_unsubscribed.toLocaleString()} unsubscribed`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                )}
              </>
            )}
            {queue &&
              (queueDown ? (
                <span className={style.error}>
                  Email queue is unavailable right now, so emails can't be
                  sent. Try again in a few minutes.
                </span>
              ) : !queue.worker_online ? (
                <span className={style.warn}>
                  The email worker is offline. Emails will wait in the queue
                  and go out once it is running again.
                </span>
              ) : queue.waiting > 0 ? (
                <span className={style.muted}>
                  {queue.waiting.toLocaleString()} earlier email
                  {queue.waiting === 1 ? " is" : "s are"} still waiting in the
                  queue, so this one may take a little longer.
                </span>
              ) : null)}
          </div>

          <label className={style.field}>
            <span className={style.label}>Subject</span>
            <input
              className={style.input}
              value={subject}
              maxLength={200}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Shown in the inbox"
            />
          </label>

          <label className={style.field}>
            <span className={style.label}>Heading (optional)</span>
            <input
              className={style.input}
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Large heading inside the email"
            />
          </label>

          <label className={style.field}>
            <span className={style.label}>Message</span>
            <textarea
              className={`${style.input} ${style.textarea}`}
              value={message}
              maxLength={10000}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Plain text. Line breaks are kept."
            />
          </label>

          <label className={style.field}>
            <span className={style.label}>Send test to (optional)</span>
            <input
              className={style.input}
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder={
                counts?.test_default_email
                  ? `Leave blank to send to ${counts.test_default_email}`
                  : "Leave blank to use the default address"
              }
            />
          </label>
        </div>

        <div className={style.footer}>
          <p className={style.hint}>
            Each email greets the user by name and includes an Unsubscribe
            link.
          </p>
          <div className={style.actions}>
            <button
              type="button"
              className={style.ghostBtn}
              onClick={onClose}
              disabled={sending || testing}
            >
              Cancel
            </button>
            <button
              type="button"
              className={style.ghostBtn}
              onClick={handleTest}
              disabled={!canTest}
              title="Send this draft only to the test address above"
            >
              {testing ? "Sending test…" : "Send test"}
            </button>
            <button
              type="button"
              className={style.primaryBtn}
              onClick={handleSend}
              disabled={!canSend}
            >
              {sending ? "Sending…" : "Send email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
